import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { SkillPackageFormState } from "../utils/skill-package-draft"
import type { SkillPackageSummary } from "@/api/admin/skill-packages"

import { Lock, TriangleAlert } from "@/components/icons"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { SkillPackageContentTab } from "./skill-package-content-tab"
import { SkillPackageFormError } from "./skill-package-editor-feedback"
import { SkillPackageEvalsTab } from "./skill-package-evals-tab"
import { SkillPackageReferencesTab } from "./skill-package-references-tab"
import { SkillPackageToolPicker } from "./skill-package-tool-picker"
import {
  useCreateSkillPackageMutation,
  usePublishSkillPackageMutation,
  useUpdateSkillPackageMutation,
} from "../hooks/queries/skill-packages"
import {
  createBlankSkillPackageForm,
  skillPackageBudgets,
  slugifySkillPackageName,
  toSkillPackageForm,
  toSkillPackagePayload,
  validateSkillPackageForm,
} from "../utils/skill-package-draft"
import { describeSkillPackageError, type SkillPackageOperation } from "../utils/skill-packages"

// The skill-package editor (#3074) — the portal's own authoring path.
//
// Before this, the only way to create a package was to know the SKILL.md
// format and build a zip outside the product. The backend could already do
// everything (create draft / edit draft / publish); nothing called it.
//
// The shape of the view follows the shape of the domain:
//
// * The DRAFT is the workspace, and a published version is frozen. When
//   `latest_version > 0` the header says which version is live and the editor
//   makes clear that saving prepares the NEXT one — it never mutates v1.
// * Every cap the backend enforces is shown as a live budget rather than
//   discovered as a 400 after the admin wrote 70 kB of instructions.
// * Saving and publishing are separate acts. Publish is deliberately not a
//   side effect of Save: freezing a version is what installed agents pin.

type EditorProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** The package to edit, or undefined to create a new one. */
  pkg?: SkillPackageSummary
}

type EditorTab = "content" | "references" | "tools" | "evals"

export function SkillPackageEditorDialog({ isOpen, onOpenChange, pkg }: EditorProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState<SkillPackageFormState>(() =>
    pkg ? toSkillPackageForm(pkg) : createBlankSkillPackageForm()
  )
  const [activeTab, setActiveTab] = useState<EditorTab>("content")
  const [showErrors, setShowErrors] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [nameTouched, setNameTouched] = useState(Boolean(pkg))

  const createMutation = useCreateSkillPackageMutation()
  const updateMutation = useUpdateSkillPackageMutation()
  const publishMutation = usePublishSkillPackageMutation()
  const busy = createMutation.isPending || updateMutation.isPending || publishMutation.isPending

  // Re-seed whenever the dialog opens on a different package: the editor is
  // mounted by the card and would otherwise keep the previous package's form.
  useEffect(() => {
    if (!isOpen) return
    setForm(pkg ? toSkillPackageForm(pkg) : createBlankSkillPackageForm())
    setActiveTab("content")
    setShowErrors(false)
    setError(null)
    setNotice(null)
    setNameTouched(Boolean(pkg))
  }, [isOpen, pkg])

  const validation = useMemo(() => validateSkillPackageForm(form), [form])
  const budgets = useMemo(() => skillPackageBudgets(form), [form])
  const isPublished = form.latestVersion > 0
  const isSaved = Boolean(form.id)

  const patch = (partial: Partial<SkillPackageFormState>) => {
    setForm((current) => ({ ...current, ...partial }))
    setNotice(null)
  }

  const showError = (exc: unknown, operation: SkillPackageOperation) => {
    const described = describeSkillPackageError(exc, operation)
    const text = t(described.key, { defaultValue: described.defaultValue })
    setError(described.detail && described.detail !== text ? `${text} (${described.detail})` : text)
  }

  const onSave = async () => {
    if (validation.form.length > 0) {
      setShowErrors(true)
      setError(null)
      return
    }
    setError(null)
    setNotice(null)
    const payload = toSkillPackagePayload(form)
    try {
      if (form.id) {
        const saved = await updateMutation.mutateAsync({
          packageId: form.id,
          payload: payload as Record<string, unknown> & { revision: number },
        })
        // The server bumps the revision on every write; keeping the stale one
        // would make the very next save (or publish) a 409.
        setForm((current) => ({ ...current, revision: saved.revision }))
        setNotice(
          t("adminDashboard.skillPackages.editor.saved", {
            defaultValue: "Draft saved. Publish it to make this version installable.",
          })
        )
      } else {
        const created = await createMutation.mutateAsync(payload)
        setForm((current) => ({
          ...current,
          id: created.id,
          revision: created.revision,
          latestVersion: created.latest_version,
        }))
        setNotice(
          t("adminDashboard.skillPackages.editor.created", {
            defaultValue:
              "Created {{name}} as a draft. Publish it to make it installable on agents.",
            name: created.name,
          })
        )
      }
    } catch (exc) {
      showError(exc, form.id ? "update" : "create")
    }
  }

  const onPublish = async () => {
    if (!form.id) return
    setError(null)
    setNotice(null)
    try {
      const version = await publishMutation.mutateAsync({
        packageId: form.id,
        revision: form.revision,
      })
      // Publishing bumps the head's revision too — refresh both so the editor
      // stays a usable workspace for the NEXT version.
      setForm((current) => ({
        ...current,
        latestVersion: version.version,
        revision: current.revision + 1,
      }))
      setNotice(
        t("adminDashboard.skillPackages.editor.published", {
          defaultValue: "Published v{{version}}. It is frozen; the draft stays your workspace.",
          version: version.version,
        })
      )
    } catch (exc) {
      showError(exc, "publish")
    }
  }

  const onNameChange = (value: string) => {
    setNameTouched(true)
    patch({ name: value })
  }

  // Until the admin edits the slug themselves, derive it from the name they are
  // typing — a first-time author should not have to learn the slug rule to get
  // past the first field.
  const onDisplayNameChange = (value: string) =>
    patch(
      nameTouched
        ? { displayName: value }
        : { displayName: value, name: slugifySkillPackageName(value) }
    )

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[880px]" data-testid="skill-package-editor">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {pkg
              ? t("adminDashboard.skillPackages.editor.editTitle", {
                  defaultValue: "Edit skill package",
                })
              : t("adminDashboard.skillPackages.editor.newTitle", {
                  defaultValue: "New skill package",
                })}
            {form.name ? <code className="font-mono text-sm">{form.name}</code> : null}
            {isPublished ? (
              <Badge data-testid="skill-package-version-badge" variant="secondary">
                <Lock aria-hidden className="size-3" />
                {t("adminDashboard.skillPackages.editor.publishedBadge", {
                  defaultValue: "v{{version}} published",
                  version: form.latestVersion,
                })}
              </Badge>
            ) : (
              <Badge variant="outline">
                {t("adminDashboard.skillPackages.editor.draftBadge", { defaultValue: "Draft" })}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isPublished
              ? t("adminDashboard.skillPackages.editor.publishedDescription", {
                  defaultValue:
                    "v{{version}} is frozen — agents that installed it keep exactly those bytes. The draft below is your workspace for the next version.",
                  version: form.latestVersion,
                })
              : t("adminDashboard.skillPackages.editor.description", {
                  defaultValue:
                    "Write the skill here and save it as many times as you like. Publishing freezes a version that agents can install.",
                })}
          </DialogDescription>
        </DialogHeader>

        {/*
          `display: contents` is load-bearing, not tidiness. A <fieldset> can be
          SIZED by a flex parent but does not pass that height down as a
          definite height to its own flex children, so the scroll body grew to
          its content height (measured 1130px inside a 918px dialog) and the
          footer painted on top of the last field. Taking the fieldset out of
          the layout makes Tabs a direct flex child of DialogContent, which does
          clamp it. `disabled`/`aria-busy` are DOM semantics and still apply to
          every descendant with no box of their own.

          The hosted-agent wizard has the same fieldset chain and looks fine
          only because its tallest tab still fits — it has the same latent bug,
          not a different structure.
        */}
        <fieldset aria-busy={busy} className="contents" disabled={busy}>
          <Tabs
            className="flex min-h-0 flex-1 flex-col"
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as EditorTab)}
          >
            <TabsList className="flex-wrap">
              <TabsTrigger value="content">
                {t("adminDashboard.skillPackages.editor.tabs.content", { defaultValue: "Skill" })}
              </TabsTrigger>
              <TabsTrigger value="references">
                {t("adminDashboard.skillPackages.editor.tabs.references", {
                  defaultValue: "References",
                })}
                {form.references.length > 0 ? ` (${form.references.length})` : ""}
              </TabsTrigger>
              <TabsTrigger value="tools">
                {t("adminDashboard.skillPackages.editor.tabs.tools", { defaultValue: "Tools" })}
                {form.recommendedTools.length > 0 ? ` (${form.recommendedTools.length})` : ""}
              </TabsTrigger>
              <TabsTrigger value="evals">
                {t("adminDashboard.skillPackages.editor.tabs.evals", {
                  defaultValue: "Eval cases",
                })}
                {form.evalCases.length > 0 ? ` (${form.evalCases.length})` : ""}
              </TabsTrigger>
            </TabsList>

            <DialogBody className="pt-4">
              <TabsContent className="mt-0" value="content">
                <SkillPackageContentTab
                  budgets={budgets}
                  busy={busy}
                  form={form}
                  isSaved={isSaved}
                  patch={patch}
                  showErrors={showErrors}
                  validation={validation}
                  onDisplayNameChange={onDisplayNameChange}
                  onGeneratedDraft={setForm}
                  onNameChange={onNameChange}
                />
              </TabsContent>

              <TabsContent className="mt-0" value="references">
                <SkillPackageReferencesTab
                  budgets={budgets}
                  form={form}
                  patch={patch}
                  showErrors={showErrors}
                  validation={validation}
                />
              </TabsContent>

              <TabsContent className="mt-0" value="tools">
                <SkillPackageToolPicker
                  recommended={form.recommendedTools}
                  required={form.requiredTools}
                  onChange={({ recommended, required }) =>
                    patch({ recommendedTools: recommended, requiredTools: required })
                  }
                />
                <SkillPackageFormError
                  codes={validation.form}
                  match={["too_many_recommended_tools", "too_many_required_tools"]}
                  show={showErrors}
                />
              </TabsContent>

              <TabsContent className="mt-0" value="evals">
                <SkillPackageEvalsTab
                  budgets={budgets}
                  form={form}
                  patch={patch}
                  showErrors={showErrors}
                  validation={validation}
                />
              </TabsContent>
            </DialogBody>
          </Tabs>
        </fieldset>

        {error ? (
          <Alert
            className="border-foreground bg-destructive text-destructive-foreground dark:border-foreground [&>svg]:text-destructive-foreground"
            data-testid="skill-package-editor-error"
            variant="destructive"
          >
            <TriangleAlert className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {notice ? (
          <p
            className="text-muted-foreground px-1 text-sm"
            data-testid="skill-package-editor-notice"
          >
            {notice}
          </p>
        ) : null}

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("adminDashboard.skillPackages.editor.close", { defaultValue: "Close" })}
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              data-testid="skill-package-save"
              disabled={busy}
              type="button"
              variant="outline"
              onClick={() => void onSave()}
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <Spinner className="size-4" />
              ) : null}
              {t("adminDashboard.skillPackages.editor.save", { defaultValue: "Save draft" })}
            </Button>
            <Button
              data-testid="skill-package-publish"
              disabled={busy || !isSaved}
              type="button"
              onClick={() => void onPublish()}
            >
              {publishMutation.isPending ? <Spinner className="size-4" /> : null}
              {isPublished
                ? t("adminDashboard.skillPackages.editor.publishNext", {
                    defaultValue: "Publish v{{version}}",
                    version: form.latestVersion + 1,
                  })
                : t("adminDashboard.skillPackages.editor.publishFirst", {
                    defaultValue: "Publish v1",
                  })}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
